import { defaultStateDescription, getTransitionsToStateFn } from '../../util/transaction.default';
import { delayedStateDescription } from '../../util/transaction.delayed';

// Reuse the same derivation the transaction utils use, applied to both processes.
const toDefault = getTransitionsToStateFn(defaultStateDescription);
const toDelayed = getTransitionsToStateFn(delayedStateDescription);

// UI status key -> the process STATE names whose entering-transitions represent it.
// State name strings mirror the (module-private) STATE_* constants in the process files.
const STATUS_STATES = {
  pending: ['preauthorized', 'pending-payment', 'payment-pending', 'confirm-payment'],
  confirmed: ['accepted', 'payed'],
  completed: ['delivered', 'reviewed', 'reviewed-by-customer', 'reviewed-by-provider'],
  cancelled: ['canceled', 'declined', 'withdrawn'],
  'payment-failed': ['payment-expired', 'payment-failed', 'expired-payment-intent'],
  // delivered, awaiting review — same server filter as "completed start"; role refinement is display-only.
  'review-pending': ['delivered'],
};

// Order shown in the filter dropdown. `unread` is intentionally NOT here:
// it is an app-metadata highlight, not a server-filterable status (see spec).
export const STATUS_ORDER = [
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'payment-failed',
  'review-pending',
];

// Status pill colors (dot / background / foreground) + i18n label ids.
// Hex values are the VanSite design-system status colors from the handoff README.
export const STATUS_TOKENS = {
  pending: { dot: '#FFAA00', bg: '#FFF7E6', fg: '#9A6A00', labelId: 'InboxPage.status.pending' },
  confirmed: { dot: '#00B24B', bg: '#E7F8EE', fg: '#0A7E3E', labelId: 'InboxPage.status.confirmed' },
  completed: { dot: '#3A6670', bg: '#EAF0F1', fg: '#0E3B43', labelId: 'InboxPage.status.completed' },
  cancelled: { dot: '#828282', bg: '#F1F3F4', fg: '#5A6470', labelId: 'InboxPage.status.cancelled' },
  'payment-failed': { dot: '#DA0000', bg: '#FDECEC', fg: '#C01616', labelId: 'InboxPage.status.paymentFailed' },
  'review-pending': { dot: '#E7AC1D', bg: '#FCF6E1', fg: '#9A6A00', labelId: 'InboxPage.status.reviewPending' },
  unread: { dot: '#2D83EE', bg: '#E9F2FE', fg: '#1B63C4', labelId: 'InboxPage.status.unread' },
};

export const SORT_OPTIONS = [
  { key: 'last-activity', apiField: 'lastTransitionedAt', labelId: 'InboxPage.sort.lastActivity' },
  { key: 'check-in', apiField: 'bookingStart', labelId: 'InboxPage.sort.checkIn' },
  { key: 'created', apiField: 'createdAt', labelId: 'InboxPage.sort.created' },
];

const DEFAULT_SORT = SORT_OPTIONS[0];

/**
 * Union of `lastTransitions` (both processes) for the selected status keys.
 * Empty array => no server filter (= all transactions for the role).
 * @param {string[]} statusKeys
 * @returns {string[]}
 */
export const statusKeysToLastTransitions = (statusKeys = []) => {
  const set = new Set();
  statusKeys.forEach((key) => {
    const states = STATUS_STATES[key];
    if (!states) return; // flag-only keys (e.g. unread) contribute nothing
    toDefault(states).forEach((t) => set.add(t));
    toDelayed(states).forEach((t) => set.add(t));
  });
  return [...set];
};

/**
 * UI sort key + direction -> native API `sort` string ('-' prefix = descending).
 * @param {string} sortKey
 * @param {'asc'|'desc'} dir
 * @returns {string}
 */
export const sortParam = (sortKey, dir) => {
  const opt = SORT_OPTIONS.find((s) => s.key === sortKey) || DEFAULT_SORT;
  return `${dir === 'desc' ? '-' : ''}${opt.apiField}`;
};
